import { API_URL } from '../config/env';
import { apiFetch } from './apiFetchPolicy';

/**
 * Get all blog posts
 */
export const getBlogPosts = async (token = null) => {
  try {
    const data = await apiFetch(`${API_URL}/blog/posts`, { token, method: 'GET' });
    return data;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
};

/**
 * Get a single blog post by ID
 */
export const getBlogPost = async (postId, token = null) => {
  try {
    return await apiFetch(`${API_URL}/blog/posts/${postId}`, { token, method: 'GET' });
  } catch (error) {
    console.error('Error fetching blog post:', error);
    throw error;
  }
};

/**
 * Ensure visibility is sent in a format backends may expect (camelCase + snake_case booleans).
 */
function withVisibilityPayload(data) {
  const v = data.visibility || 'public';
  return {
    ...data,
    visibility: v,
    visibility_public: v === 'public',
    visibility_private: v === 'private',
    visibility_logged_in: v === 'logged_in',
    visibility_listed: v === 'listed',
  };
}

/**
 * Create a new blog post
 */
export const createBlogPost = async (postData, token) => {
  if (!token) {
    throw new Error('Authentication required to create blog post');
  }
  const payload = withVisibilityPayload(postData);
  try {
    return await apiFetch(`${API_URL}/blog/posts`, {
      token,
      method: 'POST',
      body: payload,
    });
  } catch (error) {
    console.error('Error creating blog post:', error);
    throw error;
  }
};

/**
 * Update an existing blog post
 */
export const updateBlogPost = async (postId, postData, token) => {
  if (!token) {
    throw new Error('Authentication required to update blog post');
  }
  const payload = withVisibilityPayload(postData);
  try {
    return await apiFetch(`${API_URL}/blog/posts/${postId}`, {
      token,
      method: 'PUT',
      body: payload,
    });
  } catch (error) {
    console.error('Error updating blog post:', error);
    throw error;
  }
};

/**
 * Upload a file for blog (image, audio, etc.). type: 'blog-image' | 'blog-audio' | 'blog-file'
 *
 * NOTE: the multipart FormData body is passed through untouched — apiFetch does
 * NOT set Content-Type for FormData, so the browser attaches the correct
 * multipart boundary (the original code set it manually without a boundary,
 * which is strictly less correct).
 */
export const uploadBlogFile = async (file, token, fileType = 'blog-file') => {
  if (!token) {
    throw new Error('Authentication required to upload file');
  }
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', fileType);

  const data = await apiFetch(`${API_URL}/auth/upload`, {
    token,
    method: 'POST',
    body: formData,
  });
  return data?.url || data?.fileUrl || data?.file?.url || null;
};

/** Upload blog cover image (convenience wrapper) */
export const uploadBlogImage = async (file, token) => uploadBlogFile(file, token, 'blog-image');

/**
 * Delete a blog post
 */
export const deleteBlogPost = async (postId, token) => {
  if (!token) {
    throw new Error('Authentication required to delete blog post');
  }
  
  try {
    return await apiFetch(`${API_URL}/blog/posts/${postId}`, { token, method: 'DELETE' });
  } catch (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
};

/**
 * Get comments for a blog post
 */
export const getComments = async (postId, token = null) => {
  try {
    return await apiFetch(`${API_URL}/blog/posts/${postId}/comments`, { token, method: 'GET' });
  } catch (error) {
    console.error('Error fetching comments:', error);
    throw error;
  }
};

/**
 * Add a comment to a blog post
 */
export const addComment = async (postId, commentData, token = null) => {
  try {
    return await apiFetch(`${API_URL}/blog/posts/${postId}/comments`, {
      token,
      method: 'POST',
      body: commentData,
    });
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

/**
 * Update a comment
 */
export const updateComment = async (postId, commentId, commentData, token) => {
  if (!token) {
    throw new Error('Authentication required to update comment');
  }
  
  try {
    return await apiFetch(`${API_URL}/blog/posts/${postId}/comments/${commentId}`, {
      token,
      method: 'PUT',
      body: commentData,
    });
  } catch (error) {
    console.error('Error updating comment:', error);
    throw error;
  }
};

/**
 * Delete a comment
 */
export const deleteComment = async (postId, commentId, token) => {
  if (!token) {
    throw new Error('Authentication required to delete comment');
  }
  
  try {
    return await apiFetch(`${API_URL}/blog/posts/${postId}/comments/${commentId}`, {
      token,
      method: 'DELETE',
    });
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
};
